import axios, { AxiosError, AxiosInstance, AxiosResponse } from 'axios'
import { logout } from '../features/auth/authSplice'
import { hydrateProfile } from '../features/profile/actions'
import { getForbiddenFallbackRoute, isRouteAccessible } from '../features/profile/routeAccess'
import {store} from '../store/store'
import {showSnack} from '../features/snacks/snackService'
import {ErrorData} from './types';
import { locationHistory } from '../helpers/location-history';

const PROFILE_REFRESH_HEADER = 'X-Profile-Refresh-Request';
export const SILENT_ERROR_HEADER = 'X-Silent-Error';
const PROFILE_REFRESH_RETRY_HEADER = 'X-Profile-Refresh-Retry';
const PLANNED_HOURS_ERROR_REGEX = /Absence hours must match planned hours for this day \(([-+]?\d+(?:\.\d+)?)h\)\./;

let profileRefreshPromise: Promise<void> | null = null;

const formatErrorMessage = (message: string): string => {
    return message.replace(PLANNED_HOURS_ERROR_REGEX, (_match, hours: string) => {
        const parsedHours = Number(hours);
        if (!Number.isFinite(parsedHours)) {
            return _match;
        }

        return `Absence hours must match planned hours for this day (${parsedHours.toFixed(1)}h).`;
    });
};

export const configureAxios = (obsolete?: boolean): AxiosInstance => {
    const axiosInstance: AxiosInstance = axios.create()
    const host: string = obsolete ? typeof process.env.REACT_APP_BACKEND_OBSOLETE_URL == 'string' ? process.env.REACT_APP_BACKEND_OBSOLETE_URL : ''
        : typeof process.env.REACT_APP_BACKEND_URL == 'string' ? process.env.REACT_APP_BACKEND_URL : ''
    axiosInstance.defaults.baseURL = host

    axiosInstance.interceptors.response.use((response: AxiosResponse) => {
        return response
    }, async (error: AxiosError<ErrorData[]>): Promise<AxiosResponse<any>> => {
        if (!error.response) {
            return Promise.reject(error);
        }

        const freeRequests = ['login']
        const requestUrl = getRequestPath(error.config?.url)
        const isFreeRequest = Boolean(requestUrl && freeRequests.includes(requestUrl))
        const isProfileRefreshRequest = hasRequestFlag(error.config, PROFILE_REFRESH_HEADER)
        const shouldHideError = hasRequestFlag(error.config, SILENT_ERROR_HEADER)
        let suppressCurrentError = shouldHideError

        if (error.response.status === 401) {
            if (authToken() && !isFreeRequest) {
                const logoutMe = () => store.dispatch(logout())
                logoutMe()
            }
        } else if (error.response.status === 403 && authToken() && !isFreeRequest) {
            if (isProfileRefreshRequest) {
                const logoutMe = () => store.dispatch(logout())
                logoutMe()
            } else {
                const profileWasRefreshed = await refreshProfile(axiosInstance)
                    .then(() => true)
                    .catch(() => false);

                if (profileWasRefreshed) {
                    const currentProfile = store.getState().profile;
                    const currentPathname = locationHistory.location?.pathname;

                    suppressCurrentError = true;

                    if (!isRouteAccessible(currentProfile, currentPathname)) {
                        navigateToAccessibleFallback(currentProfile, currentPathname);
                        return Promise.reject(error);
                    }

                    if (isRetryableGetRequest(error.config)) {
                        return retryRequestWithFreshProfile(axiosInstance, error.config);
                    }
                }
            }
        }

        const message = error.response.data !== undefined && error.response.data.length > 0 ? formatErrorMessage(error.response.data[0].messages[0]) : error.response.statusText;
        if (!suppressCurrentError && message !== '') {
            showSnack(message, 'error')
        }

        return Promise.reject(error)
    })

    axiosInstance.interceptors.request.use((config)=>{
        const token = authToken()
        const isLoggedIn = !!token
        const freeRequests = ['login']
        const isFreeRequest = config.url && freeRequests.includes(config.url.split('?')[0])
        if (isLoggedIn && !isFreeRequest) {
            config['headers'] = config.headers ?? {};
            config.headers['Authorization'] = `Bearer ${token}`;
        } else {
            config['headers'] = config.headers ?? {};
        }
        if (!isLoggedIn && !isFreeRequest) {
            config.signal = AbortSignal.timeout(0);
            return config;
        }
        return config
    })

    return axiosInstance
}

function authToken() {
    return store.getState().auth.token
}

function getRequestPath(url?: string): string {
    if (!url) {
        return '';
    }

    return url.split('?')[0];
}

function hasRequestFlag(config: AxiosError['config'], headerName: string): boolean {
    const headerValue = config?.headers?.[headerName];
    return headerValue === 'true' || headerValue === true;
}

function getRequestMethod(config: AxiosError['config']): string {
    return (config?.method ?? 'get').toUpperCase();
}

function isRetryableGetRequest(config: AxiosError['config']): boolean {
    if (!config) {
        return false;
    }

    return getRequestMethod(config) === 'GET'
        && !hasRequestFlag(config, PROFILE_REFRESH_HEADER)
        && !hasRequestFlag(config, PROFILE_REFRESH_RETRY_HEADER);
}

async function refreshProfile(axiosInstance: AxiosInstance): Promise<void> {
    if (profileRefreshPromise) {
        return profileRefreshPromise;
    }

    profileRefreshPromise = axiosInstance
        .get('profile', {
            headers: {
                [PROFILE_REFRESH_HEADER]: 'true',
                [SILENT_ERROR_HEADER]: 'true',
            },
        })
        .then((response) => {
            store.dispatch(hydrateProfile(response.data));
        })
        .finally(() => {
            profileRefreshPromise = null;
        });

    return profileRefreshPromise;
}

function retryRequestWithFreshProfile(
    axiosInstance: AxiosInstance,
    config: AxiosError['config']
): Promise<AxiosResponse<any>> {
    if (!config) {
        return Promise.reject(new Error('Missing request config'));
    }

    config.headers = {
        ...(config.headers ?? {}),
        [PROFILE_REFRESH_RETRY_HEADER]: 'true',
    };

    return axiosInstance.request(config);
}

function navigateToAccessibleFallback(
    profile: ReturnType<typeof store.getState>['profile'],
    currentPathname: string | null | undefined
) {
    if (!locationHistory.navigate) {
        return;
    }

    const fallbackRoute = getForbiddenFallbackRoute(profile, currentPathname);
    if (fallbackRoute && fallbackRoute !== currentPathname) {
        locationHistory.navigate(fallbackRoute, { replace: true });
    }
}
